<template>
  <!-- User Account Management Modal - Following Bootstrap data-bs pattern -->
  <div class="modal fade user-management-modal"
       data-bs-backdrop="true"
       data-bs-keyboard="true"
       tabindex="-1"
       aria-labelledby="userManagementLabel"
       aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header bg-primary text-white">
          <h5 class="modal-title text-white" id="userManagementLabel">
            <i class="mdi mdi-account-cog me-2"></i>User Account Management
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading user account...</p>
          </div>

          <!-- Employee Info Header -->
          <div v-else class="mb-4">
            <div class="card border-0 bg-light">
              <div class="card-body">
                <div class="d-flex align-items-center">
                  <div class="avatar-md me-3">
                    <span class="avatar-title rounded-circle bg-primary text-white font-size-20">
                      {{getInitials(employee.firstName, employee.lastName)}}
                    </span>
                  </div>
                  <div>
                    <h5 class="mb-1">{{getFullName(employee)}}</h5>
                    <p class="text-muted mb-0">
                      <i class="mdi mdi-card-account-details me-1"></i>
                      {{employee.empNo}} | {{employee.email}}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- No User Account -->
          <div v-if="!loading && !userAccount" class="text-center py-4">
            <div class="avatar-lg mx-auto mb-4">
              <div class="avatar-title bg-light rounded-circle text-primary">
                <i class="mdi mdi-account-off font-size-36"></i>
              </div>
            </div>
            <h5>No User Account Found</h5>
            <p class="text-muted mb-4">This employee doesn't have a system user account yet.</p>

            <button @click="createNewAccount" class="btn btn-primary">
              <i class="mdi mdi-account-plus me-1"></i>Create User Account
            </button>
          </div>

          <!-- Create User Form -->
          <div v-if="showCreateForm && !userAccount" class="mt-4">
            <h6 class="mb-3">Create New User Account</h6>

            <!-- Loading Categories -->
            <div v-if="loadingCategories" class="text-center py-3">
              <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
              <span class="ms-2">Loading categories...</span>
            </div>

            <form v-else @submit.prevent="createUserAccount">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Username <span class="text-danger">*</span></label>
                  <input v-model="newUser.username" type="text" class="form-control" required>
                  <small class="text-muted">Suggested: {{employee.nationalId || employee.empNo}}</small>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">Password <span class="text-danger">*</span></label>
                  <div class="input-group">
                    <input v-model="newUser.password" :type="showPassword ? 'text' : 'password'" class="form-control" required>
                    <button @click="showPassword = !showPassword" class="btn btn-outline-secondary" type="button">
                      <i :class="showPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                    </button>
                    <button @click="generatePassword" class="btn btn-outline-primary" type="button" title="Generate Password">
                      <i class="mdi mdi-refresh"></i>
                    </button>
                  </div>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">User Category <span class="text-danger">*</span></label>
                  <select v-model="newUser.category" class="form-select" required>
                    <option value="" disabled>Select a category...</option>
                    <option v-for="category in availableCategories" :key="category.id" :value="category.categoryName">
                      {{category.categoryName}}
                      <span v-if="category.usersLimit > 0">
                        ({{category.currentUsers || 0}}/{{category.usersLimit}})
                      </span>
                      <span v-else> (Unlimited)</span>
                    </option>
                  </select>
                  <small v-if="userCategories.length === 0" class="text-warning">
                    <i class="mdi mdi-alert me-1"></i>No categories available
                  </small>
                  <small v-else-if="selectedCategoryInfo" class="text-muted d-block">
                    <i class="mdi mdi-information me-1"></i>
                    {{selectedCategoryInfo.availableSlots === -1 ? 'Unlimited slots' : selectedCategoryInfo.availableSlots + ' slot(s) available'}}
                  </small>

                  <!-- Display roles for selected category -->
                  <div v-if="selectedCategoryInfo && selectedCategoryInfo.roles && selectedCategoryInfo.roles.length > 0" class="mt-2">
                    <small class="text-muted d-block mb-1">
                      <i class="mdi mdi-shield-account me-1"></i>
                      <strong>Assigned Roles ({{selectedCategoryInfo.roles.length}}):</strong>
                    </small>
                    <div class="d-flex flex-wrap gap-1">
                      <span v-for="(role, index) in selectedCategoryInfo.roles" :key="index" class="badge bg-info text-dark bg-opacity-25">
                        {{role}}
                      </span>
                    </div>
                  </div>
                  <div v-else-if="selectedCategoryInfo && selectedCategoryInfo.roles && selectedCategoryInfo.roles.length === 0" class="mt-2">
                    <small class="text-muted">
                      <i class="mdi mdi-alert-circle me-1"></i>No roles assigned to this category
                    </small>
                  </div>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">Require OTP</label>
                  <select v-model="newUser.requireOTP" class="form-select">
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
              </div>

              <div class="d-flex justify-content-end gap-2 mt-3">
                <button @click="cancelCreateForm" type="button" class="btn btn-light">Cancel</button>
                <button type="submit" class="btn btn-primary" :disabled="creating || availableCategories.length === 0">
                  <span v-if="creating" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="mdi mdi-check me-1"></i>
                  Create Account
                </button>
              </div>
            </form>
          </div>

          <!-- Existing User Account -->
          <div v-if="!loading && userAccount && !showCreateForm">
            <!-- User Status Overview -->
            <div class="row mb-4">
              <div class="col-md-4 mb-3">
                <div class="card border-0" :class="getUserStatusCardClass(userAccount.status)">
                  <div class="card-body text-center">
                    <h6 class="text-muted mb-2">Account Status</h6>
                    <h4 :class="getUserStatusTextClass(userAccount.status)">
                      {{formatStatus(userAccount.status)}}
                    </h4>
                  </div>
                </div>
              </div>

              <div class="col-md-4 mb-3">
                <div class="card border-0 bg-light">
                  <div class="card-body text-center">
                    <h6 class="text-muted mb-2">Username</h6>
                    <h5 class="mb-0">{{userAccount.username}}</h5>
                  </div>
                </div>
              </div>

              <div class="col-md-4 mb-3">
                <div class="card border-0 bg-light">
                  <div class="card-body text-center">
                    <h6 class="text-muted mb-2">Category</h6>
                    <span class="badge bg-primary font-size-14">{{userAccount.category}}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- User Details -->
            <div class="card border mb-4">
              <div class="card-header bg-light">
                <h6 class="mb-0">Account Details</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="text-muted small">Email</label>
                    <p class="mb-0">{{userAccount.email || 'N/A'}}</p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted small">Phone Number</label>
                    <p class="mb-0">{{userAccount.phoneNumber || 'N/A'}}</p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted small">Last Login</label>
                    <p class="mb-0">{{formatDateTime(userAccount.lastLogin)}}</p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted small">Created At</label>
                    <p class="mb-0">{{formatDateTime(userAccount.createdAt)}}</p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted small">Require OTP</label>
                    <p class="mb-0">
                      <span :class="userAccount.requireOTP === 'true' ? 'badge bg-success' : 'badge bg-secondary'">
                        {{userAccount.requireOTP === 'true' ? 'Yes' : 'No'}}
                      </span>
                    </p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted small">Password Changed</label>
                    <p class="mb-0">
                      <span :class="userAccount.passwordChanged === 'true' ? 'badge bg-success' : 'badge bg-warning'">
                        {{userAccount.passwordChanged === 'true' ? 'Yes' : 'No'}}
                      </span>
                    </p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted small">Failed Login Attempts</label>
                    <p class="mb-0">
                      <span :class="getFailedAttemptsClass(userAccount.failedLoginAttempts)">
                        {{userAccount.failedLoginAttempts || 0}}
                      </span>
                    </p>
                  </div>
                  <div class="col-md-6 mb-3" v-if="userAccount.accountLockedUntil">
                    <label class="text-muted small">Account Locked Until</label>
                    <p class="mb-0 text-danger">{{formatDateTime(userAccount.accountLockedUntil)}}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Category Roles Card -->
            <div class="card border mb-4" v-if="currentUserCategoryInfo">
              <div class="card-header bg-light d-flex justify-content-between align-items-center">
                <h6 class="mb-0">
                  <i class="mdi mdi-shield-account me-2"></i>Category Permissions
                </h6>
                <span class="badge bg-primary">{{userAccount.category}}</span>
              </div>
              <div class="card-body">
                <div v-if="currentUserCategoryInfo.roles && currentUserCategoryInfo.roles.length > 0">
                  <p class="text-muted small mb-2">
                    This user has access to <strong>{{currentUserCategoryInfo.roles.length}}</strong> role(s) through their category:
                  </p>
                  <div class="d-flex flex-wrap gap-2">
                    <span v-for="(role, index) in currentUserCategoryInfo.roles" :key="index" class="badge bg-info text-dark bg-opacity-25">
                      <i class="mdi mdi-check-circle me-1"></i>{{role}}
                    </span>
                  </div>
                </div>
                <div v-else class="text-center py-3">
                  <i class="mdi mdi-alert-circle text-muted font-size-24"></i>
                  <p class="text-muted mb-0 mt-2">No roles assigned to this category</p>
                </div>
              </div>
            </div>

            <!-- Account Actions -->
            <div class="card border">
              <div class="card-header bg-light">
                <h6 class="mb-0">Account Actions</h6>
              </div>
              <div class="card-body">
                <div class="row g-3">
                  <!-- Reset Password -->
                  <div class="col-md-6">
                    <button @click="showResetPasswordForm = true" class="btn btn-warning w-100" :disabled="actionLoading">
                      <i class="mdi mdi-lock-reset me-1"></i>Reset Password
                    </button>
                  </div>

                  <!-- Unlock Account (if locked) -->
                  <div class="col-md-6" v-if="isAccountLocked()">
                    <button @click="unlockAccount" class="btn btn-info w-100" :disabled="actionLoading">
                      <span v-if="actionLoading" class="spinner-border spinner-border-sm me-1"></span>
                      <i v-else class="mdi mdi-lock-open me-1"></i>
                      Unlock Account
                    </button>
                  </div>

                  <!-- Activate/Suspend -->
                  <div class="col-md-6" v-if="userAccount.status === 'suspended'">
                    <button @click="activateAccount" class="btn btn-success w-100" :disabled="actionLoading">
                      <span v-if="actionLoading" class="spinner-border spinner-border-sm me-1"></span>
                      <i v-else class="mdi mdi-account-check me-1"></i>
                      Activate Account
                    </button>
                  </div>

                  <div class="col-md-6" v-if="userAccount.status === 'active'">
                    <button @click="showSuspendForm = true" class="btn btn-warning w-100" :disabled="actionLoading">
                      <i class="mdi mdi-account-off me-1"></i>Suspend Account
                    </button>
                  </div>

                  <!-- Update Settings -->
                  <div class="col-md-6">
                    <button @click="showUpdateFormMethod" class="btn btn-primary w-100" :disabled="actionLoading">
                      <i class="mdi mdi-account-edit me-1"></i>Update Settings
                    </button>
                  </div>

                  <!-- Delete Account -->
                  <div class="col-md-6">
                    <button @click="showDeleteConfirm = true" class="btn btn-danger w-100" :disabled="actionLoading">
                      <i class="mdi mdi-delete me-1"></i>Delete Account
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Reset Password Form -->
            <div v-if="showResetPasswordForm" class="card border border-warning mt-4">
              <div class="card-header bg-warning bg-opacity-10">
                <h6 class="mb-0 text-warning">
                  <i class="mdi mdi-lock-reset me-1"></i>Reset Password
                </h6>
              </div>
              <div class="card-body">
                <form @submit.prevent="resetPassword">
                  <div class="mb-3">
                    <label class="form-label">New Password <span class="text-danger">*</span></label>
                    <div class="input-group">
                      <input v-model="resetPasswordData.newPassword" :type="showPassword ? 'text' : 'password'" class="form-control" required>
                      <button @click="showPassword = !showPassword" class="btn btn-outline-secondary" type="button">
                        <i :class="showPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                      </button>
                      <button @click="generatePasswordForReset" class="btn btn-outline-primary" type="button">
                        <i class="mdi mdi-refresh"></i>
                      </button>
                    </div>
                  </div>

                  <div class="mb-3">
                    <div class="form-check">
                      <input v-model="resetPasswordData.forceChange" class="form-check-input" type="checkbox" id="forceChangeCheck">
                      <label class="form-check-label" for="forceChangeCheck">
                        Force password change on next login
                      </label>
                    </div>
                  </div>

                  <div class="d-flex justify-content-end gap-2">
                    <button @click="showResetPasswordForm = false; resetPasswordData = {newPassword: '', forceChange: true}" type="button" class="btn btn-light">Cancel</button>
                    <button type="submit" class="btn btn-warning" :disabled="actionLoading">
                      <span v-if="actionLoading" class="spinner-border spinner-border-sm me-1"></span>
                      <i v-else class="mdi mdi-check me-1"></i>
                      Reset Password
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <!-- Update Form -->
            <div v-if="showUpdateForm" class="card border border-primary mt-4">
              <div class="card-header bg-primary bg-opacity-10">
                <h6 class="mb-0 text-primary">
                  <i class="mdi mdi-account-edit me-1"></i>Update Account Settings
                </h6>
              </div>
              <div class="card-body">
                <!-- Loading Categories -->
                <div v-if="loadingCategories" class="text-center py-3 mb-3">
                  <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                  <span class="ms-2">Loading categories...</span>
                </div>

                <form v-else @submit.prevent="updateAccount">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Status</label>
                      <select v-model="updateData.status" class="form-select">
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="suspended">Suspended</option>
                        <option value="pending">Pending</option>
                      </select>
                    </div>

                    <div class="col-md-6 mb-3">
                      <label class="form-label">Category</label>
                      <select v-model="updateData.category" class="form-select">
                        <option v-for="category in userCategories" :key="category.id" :value="category.categoryName">
                          {{category.categoryName}}
                          <span v-if="category.usersLimit > 0">
                            ({{category.currentUsers || 0}}/{{category.usersLimit}})
                          </span>
                          <span v-else> (Unlimited)</span>
                        </option>
                      </select>
                      <small v-if="updateSelectedCategoryInfo" class="text-muted d-block">
                        <i class="mdi mdi-information me-1"></i>
                        {{updateSelectedCategoryInfo.availableSlots === -1 ? 'Unlimited slots' : updateSelectedCategoryInfo.availableSlots + ' slot(s) available'}}
                      </small>

                      <!-- Display roles for selected category -->
                      <div v-if="updateSelectedCategoryInfo && updateSelectedCategoryInfo.roles && updateSelectedCategoryInfo.roles.length > 0" class="mt-2">
                        <small class="text-muted d-block mb-1">
                          <i class="mdi mdi-shield-account me-1"></i>
                          <strong>Assigned Roles ({{updateSelectedCategoryInfo.roles.length}}):</strong>
                        </small>
                        <div class="d-flex flex-wrap gap-1">
                          <span v-for="(role, index) in updateSelectedCategoryInfo.roles" :key="index" class="badge bg-info text-dark bg-opacity-25">
                            {{role}}
                          </span>
                        </div>
                      </div>
                      <div v-else-if="updateSelectedCategoryInfo && updateSelectedCategoryInfo.roles && updateSelectedCategoryInfo.roles.length === 0" class="mt-2">
                        <small class="text-muted">
                          <i class="mdi mdi-alert-circle me-1"></i>No roles assigned to this category
                        </small>
                      </div>
                    </div>

                    <div class="col-md-6 mb-3">
                      <label class="form-label">Require OTP</label>
                      <select v-model="updateData.requireOTP" class="form-select">
                        <option value="false">No</option>
                        <option value="true">Yes</option>
                      </select>
                    </div>
                  </div>

                  <div class="d-flex justify-content-end gap-2">
                    <button @click="showUpdateForm = false" type="button" class="btn btn-light">Cancel</button>
                    <button type="submit" class="btn btn-primary" :disabled="actionLoading">
                      <span v-if="actionLoading" class="spinner-border spinner-border-sm me-1"></span>
                      <i v-else class="mdi mdi-check me-1"></i>
                      Update Settings
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <!-- Suspend Confirmation -->
            <div v-if="showSuspendForm" class="card border border-warning mt-4">
              <div class="card-header bg-warning bg-opacity-10">
                <h6 class="mb-0 text-warning">
                  <i class="mdi mdi-alert me-1"></i>Suspend Account
                </h6>
              </div>
              <div class="card-body">
                <p class="text-muted">Please provide a reason for suspending this account:</p>
                <textarea v-model="suspendReason" class="form-control mb-3" rows="3" placeholder="Reason for suspension..."></textarea>

                <div class="d-flex justify-content-end gap-2">
                  <button @click="showSuspendForm = false; suspendReason = ''" type="button" class="btn btn-light">Cancel</button>
                  <button @click="suspendAccount" class="btn btn-warning" :disabled="actionLoading">
                    <span v-if="actionLoading" class="spinner-border spinner-border-sm me-1"></span>
                    <i v-else class="mdi mdi-check me-1"></i>
                    Confirm Suspend
                  </button>
                </div>
              </div>
            </div>

            <!-- Delete Confirmation -->
            <div v-if="showDeleteConfirm" class="card border border-danger mt-4">
              <div class="card-header bg-danger bg-opacity-10">
                <h6 class="mb-0 text-danger">
                  <i class="mdi mdi-alert me-1"></i>Delete User Account
                </h6>
              </div>
              <div class="card-body">
                <div class="alert alert-danger">
                  <strong>Warning!</strong> This action will deactivate the user account. This is a soft delete and can be reversed by reactivating the account.
                </div>

                <p class="text-muted">Please provide a reason for deleting this account:</p>
                <textarea v-model="deleteReason" class="form-control mb-3" rows="3" placeholder="Reason for deletion..."></textarea>

                <div class="d-flex justify-content-end gap-2">
                  <button @click="showDeleteConfirm = false; deleteReason = ''" type="button" class="btn btn-light">Cancel</button>
                  <button @click="deleteAccount" class="btn btn-danger" :disabled="actionLoading">
                    <span v-if="actionLoading" class="spinner-border spinner-border-sm me-1"></span>
                    <i v-else class="mdi mdi-check me-1"></i>
                    Confirm Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer bg-light">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
/* Lighter backdrop for user management modal */
.user-management-modal ~ .modal-backdrop {
  background-color: rgba(0, 0, 0, 0.35) !important;
}

.user-management-modal ~ .modal-backdrop.show {
  opacity: 0.6 !important;
}

/* Better modal shadow */
.user-management-modal .modal-content {
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.25);
  border: none;
  border-radius: 0.5rem;
}
</style>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";
import moment from "moment";

export default {
  name: "UserManagementModal",
  props: {
    employee: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      creating: false,
      actionLoading: false,
      userAccount: null,
      showCreateForm: false,
      showResetPasswordForm: false,
      showUpdateForm: false,
      showSuspendForm: false,
      showDeleteConfirm: false,
      showPassword: false,
      userCategories: [],
      loadingCategories: false,
      newUser: {
        username: '',
        password: '',
        category: '',
        requireOTP: 'false'
      },
      resetPasswordData: {
        newPassword: '',
        forceChange: true
      },
      updateData: {
        status: '',
        category: '',
        requireOTP: ''
      },
      suspendReason: '',
      deleteReason: ''
    }
  },
  computed: {
    availableCategories() {
      // Filter categories that are available (not full)
      return this.userCategories.filter(cat =>
          cat.availability === 'unlimited' || cat.availability === 'available'
      );
    },
    selectedCategoryInfo() {
      if (!this.newUser.category) return null;
      return this.userCategories.find(cat => cat.categoryName === this.newUser.category);
    },
    updateSelectedCategoryInfo() {
      if (!this.updateData.category) return null;
      return this.userCategories.find(cat => cat.categoryName === this.updateData.category);
    },
    currentUserCategoryInfo() {
      if (!this.userAccount || !this.userAccount.category || this.userCategories.length === 0) return null;
      return this.userCategories.find(cat => cat.categoryName === this.userAccount.category);
    }
  },
  watch: {
    employee: {
      handler(newVal) {
        if (newVal && newVal.id) {
          this.loadUserAccount();
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    loadUserAccount() {
      if (!this.employee || !this.employee.id) {
        return;
      }

      this.loading = true;
      this.showCreateForm = false;
      this.showResetPasswordForm = false;
      this.showUpdateForm = false;
      this.showSuspendForm = false;
      this.showDeleteConfirm = false;

      const data = new FormData();
      data.append("function", "getUserByEmployeeId");
      data.append("employeeId", this.employee.id);

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.userAccount = res.data.data.user;

              // Pre-populate update form
              if (this.userAccount) {
                this.updateData = {
                  status: this.userAccount.status,
                  category: this.userAccount.category,
                  requireOTP: this.userAccount.requireOTP
                };

                // Load categories to display roles
                if (this.userCategories.length === 0) {
                  this.getUserCategories();
                }
              }
            } else {
              this.userAccount = null;
              // Suggest default username
              this.newUser.username = this.employee.nationalId || this.employee.empNo || '';
            }
          })
          .catch((e) => {
            this.loading = false;
            if (this.$toast) {
              this.$toast.error("Failed to load user account: " + (e.message || 'Unknown error'));
            }
          });
    },

    getUserCategories() {
      this.loadingCategories = true;

      const data = new FormData();
      data.append("function", "getCategories");

      execute(data, authUrl)
          .then((res) => {
            this.loadingCategories = false;
            if (res.data.success) {
              // Process categories to include availability information
              this.userCategories = (res.data.data.categories || []).map(cat => {
                const availableSlots = cat.usersLimit === 0
                    ? -1
                    : Math.max(0, cat.usersLimit - cat.currentUsers);

                return {
                  ...cat,
                  availableSlots: availableSlots,
                  availability: cat.usersLimit === 0
                      ? 'unlimited'
                      : (availableSlots > 0 ? 'available' : 'full')
                };
              });

              // Set default category if available and not already set
              if (this.userCategories.length > 0 && !this.newUser.category) {
                // Try to find "Employee" category as default
                const employeeCategory = this.userCategories.find(cat =>
                    cat.categoryName.toLowerCase() === 'employee' &&
                    (cat.availability === 'unlimited' || cat.availability === 'available')
                );
                if (employeeCategory) {
                  this.newUser.category = employeeCategory.categoryName;
                } else {
                  // Use first available category
                  const availableCategory = this.userCategories.find(cat =>
                      cat.availability === 'unlimited' || cat.availability === 'available'
                  );
                  if (availableCategory) {
                    this.newUser.category = availableCategory.categoryName;
                  }
                }
              }
            } else {
              if (this.$toast) {
                this.$toast.error("Failed to load categories: " + res.data.message);
              }
              this.userCategories = [];
            }
          })
          .catch((e) => {
            this.loadingCategories = false;
            if (this.$toast) {
              this.$toast.error("Failed to load categories: " + (e.message || 'Unknown error'));
            }
            this.userCategories = [];
          });
    },

    createNewAccount() {
      this.showCreateForm = true;
      this.getUserCategories();
    },

    showUpdateFormMethod() {
      this.showUpdateForm = true;
      if (this.userCategories.length === 0) {
        this.getUserCategories();
      }
    },

    cancelCreateForm() {
      this.showCreateForm = false;
      this.newUser = {
        username: this.employee.nationalId || this.employee.empNo || '',
        password: '',
        category: '',
        requireOTP: 'false'
      };
    },

    createUserAccount() {
      this.creating = true;

      // Get the selected category object to extract both name and ID
      const selectedCategory = this.userCategories.find(cat => cat.categoryName === this.newUser.category);

      if (!selectedCategory) {
        if (this.$toast) {
          this.$toast.error("Invalid category selected");
        }
        this.creating = false;
        return;
      }

      const data = new FormData();
      data.append("function", "createUserAccount");
      data.append("employeeId", this.employee.id);
      data.append("username", this.newUser.username);
      data.append("password", this.newUser.password);
      data.append("category", selectedCategory.categoryName);
      data.append("categoryId", selectedCategory.id);
      data.append("requireOTP", this.newUser.requireOTP);

      execute(data, authUrl)
          .then((res) => {
            this.creating = false;
            if (res.data.success) {
              if (this.$toast) {
                this.$toast.success(res.data.message);
              }
              this.showCreateForm = false;
              this.loadUserAccount();
              this.$emit('user-created');
            } else {
              if (this.$toast) {
                this.$toast.error(res.data.message);
              }
            }
          })
          .catch((e) => {
            this.creating = false;
            if (this.$toast) {
              this.$toast.error("Failed to create user account: " + (e.message || 'Unknown error'));
            }
          });
    },

    resetPassword() {
      this.actionLoading = true;

      const data = new FormData();
      data.append("function", "resetUserPassword");
      data.append("userId", this.userAccount.userID);
      data.append("newPassword", this.resetPasswordData.newPassword);
      data.append("forceChange", this.resetPasswordData.forceChange ? 'true' : 'false');

      execute(data, authUrl)
          .then((res) => {
            this.actionLoading = false;
            if (res.data.success) {
              if (this.$toast) {
                this.$toast.success(res.data.message);
              }
              this.showResetPasswordForm = false;
              this.resetPasswordData = { newPassword: '', forceChange: true };
              this.loadUserAccount();
            } else {
              if (this.$toast) {
                this.$toast.error(res.data.message);
              }
            }
          })
          .catch((e) => {
            this.actionLoading = false;
            if (this.$toast) {
              this.$toast.error("Failed to reset password: " + (e.message || 'Unknown error'));
            }
          });
    },

    updateAccount() {
      this.actionLoading = true;

      // Get the selected category object to extract both name and ID
      const selectedCategory = this.userCategories.find(cat => cat.categoryName === this.updateData.category);

      if (!selectedCategory) {
        if (this.$toast) {
          this.$toast.error("Invalid category selected");
        }
        this.actionLoading = false;
        return;
      }

      const data = new FormData();
      data.append("function", "updateUserAccount");
      data.append("userId", this.userAccount.userID);
      data.append("status", this.updateData.status);
      data.append("category", selectedCategory.categoryName);
      data.append("categoryId", selectedCategory.id);
      data.append("requireOTP", this.updateData.requireOTP);

      execute(data, authUrl)
          .then((res) => {
            this.actionLoading = false;
            if (res.data.success) {
              if (this.$toast) {
                this.$toast.success(res.data.message);
              }
              this.showUpdateForm = false;
              this.loadUserAccount();
              this.$emit('user-updated');
            } else {
              if (this.$toast) {
                this.$toast.error(res.data.message);
              }
            }
          })
          .catch((e) => {
            this.actionLoading = false;
            if (this.$toast) {
              this.$toast.error("Failed to update account: " + (e.message || 'Unknown error'));
            }
          });
    },

    unlockAccount() {
      if (!confirm('Are you sure you want to unlock this account?')) return;

      this.actionLoading = true;

      const data = new FormData();
      data.append("function", "unlockUserAccount");
      data.append("userId", this.userAccount.userID);

      execute(data, authUrl)
          .then((res) => {
            this.actionLoading = false;
            if (res.data.success) {
              if (this.$toast) {
                this.$toast.success(res.data.message);
              }
              this.loadUserAccount();
            } else {
              if (this.$toast) {
                this.$toast.error(res.data.message);
              }
            }
          })
          .catch((e) => {
            this.actionLoading = false;
            if (this.$toast) {
              this.$toast.error("Failed to unlock account: " + (e.message || 'Unknown error'));
            }
          });
    },

    suspendAccount() {
      this.actionLoading = true;

      const data = new FormData();
      data.append("function", "suspendUserAccount");
      data.append("userId", this.userAccount.userID);
      data.append("reason", this.suspendReason);

      execute(data, authUrl)
          .then((res) => {
            this.actionLoading = false;
            if (res.data.success) {
              if (this.$toast) {
                this.$toast.success(res.data.message);
              }
              this.showSuspendForm = false;
              this.suspendReason = '';
              this.loadUserAccount();
              this.$emit('user-suspended');
            } else {
              if (this.$toast) {
                this.$toast.error(res.data.message);
              }
            }
          })
          .catch((e) => {
            this.actionLoading = false;
            if (this.$toast) {
              this.$toast.error("Failed to suspend account: " + (e.message || 'Unknown error'));
            }
          });
    },

    activateAccount() {
      if (!confirm('Are you sure you want to activate this account?')) return;

      this.actionLoading = true;

      const data = new FormData();
      data.append("function", "activateUserAccount");
      data.append("userId", this.userAccount.userID);

      execute(data, authUrl)
          .then((res) => {
            this.actionLoading = false;
            if (res.data.success) {
              if (this.$toast) {
                this.$toast.success(res.data.message);
              }
              this.loadUserAccount();
              this.$emit('user-activated');
            } else {
              if (this.$toast) {
                this.$toast.error(res.data.message);
              }
            }
          })
          .catch((e) => {
            this.actionLoading = false;
            if (this.$toast) {
              this.$toast.error("Failed to activate account: " + (e.message || 'Unknown error'));
            }
          });
    },

    deleteAccount() {
      this.actionLoading = true;

      const data = new FormData();
      data.append("function", "deleteUserAccount");
      data.append("userId", this.userAccount.userID);
      data.append("reason", this.deleteReason);

      execute(data, authUrl)
          .then((res) => {
            this.actionLoading = false;
            if (res.data.success) {
              if (this.$toast) {
                this.$toast.success(res.data.message);
              }
              this.showDeleteConfirm = false;
              this.deleteReason = '';
              this.loadUserAccount();
              this.$emit('user-deleted');
            } else {
              if (this.$toast) {
                this.$toast.error(res.data.message);
              }
            }
          })
          .catch((e) => {
            this.actionLoading = false;
            if (this.$toast) {
              this.$toast.error("Failed to delete account: " + (e.message || 'Unknown error'));
            }
          });
    },

    generatePassword() {
      const length = 12;
      const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
      let password = "";
      for (let i = 0; i < length; i++) {
        password += charset.charAt(Math.floor(Math.random() * charset.length));
      }
      this.newUser.password = password;
    },

    generatePasswordForReset() {
      const length = 12;
      const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
      let password = "";
      for (let i = 0; i < length; i++) {
        password += charset.charAt(Math.floor(Math.random() * charset.length));
      }
      this.resetPasswordData.newPassword = password;
    },

    isAccountLocked() {
      if (!this.userAccount || !this.userAccount.accountLockedUntil) return false;
      return moment(this.userAccount.accountLockedUntil).isAfter(moment());
    },

    getInitials(firstName, lastName) {
      return ((firstName ? firstName.charAt(0) : '') + (lastName ? lastName.charAt(0) : '')).toUpperCase();
    },

    getFullName(emp) {
      const parts = [emp.firstName, emp.middleName, emp.lastName].filter(Boolean);
      return parts.join(' ') || 'N/A';
    },

    formatStatus(status) {
      if (!status) return 'Unknown';
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    },

    getUserStatusCardClass(status) {
      const statusLower = status?.toLowerCase();
      if (statusLower === 'active') return 'bg-success bg-opacity-10';
      if (statusLower === 'suspended') return 'bg-warning bg-opacity-10';
      if (statusLower === 'inactive') return 'bg-danger bg-opacity-10';
      return 'bg-secondary bg-opacity-10';
    },

    getUserStatusTextClass(status) {
      const statusLower = status?.toLowerCase();
      if (statusLower === 'active') return 'text-success';
      if (statusLower === 'suspended') return 'text-warning';
      if (statusLower === 'inactive') return 'text-danger';
      return 'text-secondary';
    },

    getFailedAttemptsClass(attempts) {
      if (!attempts || attempts === 0) return 'badge bg-success';
      if (attempts < 3) return 'badge bg-warning';
      return 'badge bg-danger';
    },

    formatDateTime(datetime) {
      if (!datetime) return 'N/A';
      return moment(datetime).format('DD MMM YYYY, HH:mm');
    }
  }
}
</script>

<style scoped>
.avatar-md {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-lg {
  width: 64px;
  height: 64px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.card {
  transition: all 0.3s ease;
}

.form-check-input:checked {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.modal-dialog-scrollable .modal-body {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}
</style>